import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { notify } from "./Toast";
import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import CalendarSearch from "./CalendarSearch";
import NudgeComposer from "./NudgeComposer";
import { useAuth } from "../hooks/useAuth";
import { buildBins, DueDates, fmtDueDate, fmtTaskRecurrence, isOverdue, OpenProjectLink, POLL_MS, } from "./scheduler/schedulerShared";
import { PersonalTodoCell } from "./scheduler/PersonalTodoCell";
import { SchedulerGoalsCell } from "./scheduler/SchedulerGoalsCell";
import { ProjectCard, RecurringPatternRow } from "./scheduler/ProjectCard";
import { ReassignHandoffModal } from "./scheduler/ReassignHandoffModal";
import { KillListTabView, ParkingLotTabView } from "./scheduler/TabViews";
export default function SchedulableItemsPanel({ open, onClose, onScheduleAsBlock, onScheduleTaskAsBlock, onScheduleProjectAsBlock, onScheduleAsRecurring, onScheduleKillItem, onSchedulePersonalTodo, onScheduleGoal, onScheduleGoalDays, refreshTick, onScheduled, }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    // Error state for the main schedulable-items fetch. Without this a
    // failed load left the panel stuck on "Loading…" forever — fetchOnce
    // had no catch, so the rejected promise was unhandled and `loading`
    // never cleared.
    const [fetchError, setFetchError] = useState(null);
    const [claiming, setClaiming] = useState(null);
    // Per-session sort preference for the items+tasks lists inside each project.
    // The dropdown that mutated this was removed per spec; the value stays
    // fixed at "due_date" but we keep the state so ProjectCard's compare
    // functions don't need a rewrite.
    const [sort] = useState("due_date");
    // Four widgets render side-by-side in a 2×2 grid — Mgmt Assigned, Kill
    // List, Parking Lot, and Recurring events all visible at the same time.
    // The old single-tab view is gone; everything loads on open so the user
    // can see what's in each bucket without clicking between tabs.
    // Kill List + Parking Lot fetched independently from the main /me/schedulable
    // items endpoint so each tab can refresh on its own cadence and we don't
    // bloat the shared poll. Loaded lazily when the tab is first opened.
    const [killList, setKillList] = useState([]);
    // One-off tasks the caller owns / is assigned to — surfaced as a
    // dedicated "One Off Tasks" section under Mgmt Assigned so they can be
    // added to the Kill List (and scheduled) like any other assigned work.
    const [oneOffTasks, setOneOffTasks] = useState([]);
    const [unassignedBuProjects, setUnassignedBuProjects] = useState([]);
    // Individually-unassigned tasks inside the caller's BU — populated
    // by the same /parking-lot fetch (server returns them as
    // `unassigned_bu_tasks`). Surfaced alongside the project-level
    // bucket so partially-claimed projects don't hide their orphan
    // tasks. See backend/routes/parking_lot.py docstring for the
    // discovery rule.
    const [unassignedBuTasks, setUnassignedBuTasks] = useState([]);
    const [killListLoaded, setKillListLoaded] = useState(false);
    const [parkingLotLoaded, setParkingLotLoaded] = useState(false);
    // Kill List row a user has chosen to reassign — null when the modal is
    // closed. Holds the full KillListItem so the modal can show context
    // (task title, project name) without re-fetching.
    const [reassignKillRow, setReassignKillRow] = useState(null);
    // The blockers behind a waiting row the user clicked "Nudge" on — null
    // when the NudgeComposer is closed. The composer turns these into a
    // "who to nudge about what" choice.
    const [nudgeBlockers, setNudgeBlockers] = useState(null);
    // Which widget cells are currently expanded. All four start collapsed
    // so the panel stays compact on open; the user clicks the +/− button
    // to expand the ones they care about.
    const [expandedWidgets, setExpandedWidgets] = useState(new Set());
    function toggleWidget(id) {
        setExpandedWidgets((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }
    const { user: currentUser } = useAuth();
    const [tick, setTick] = useState(0);
    const [lastFetchedAt, setLastFetchedAt] = useState(null);
    // Centralized fetcher so polling, focus, manual refresh, and the open/refreshTick
    // effects all go through the same code path.
    useEffect(() => {
        if (!open)
            return;
        let cancelled = false;
        async function fetchOnce(showLoading) {
            if (showLoading)
                setLoading(true);
            try {
                const d = await api.get("/users/me/schedulable-items");
                if (!cancelled) {
                    setData(d);
                    setLastFetchedAt(new Date());
                    setFetchError(null);
                }
            }
            catch (e) {
                // Surface the failure instead of leaving an unhandled rejection
                // and a spinner that never resolves. Background polls
                // (showLoading=false) also land here — if a poll fails we keep
                // the stale data on screen but flag the error so the user knows
                // it's not live.
                if (!cancelled) {
                    setFetchError(e instanceof Error
                        ? e.message
                        : "Couldn't load your schedulable items");
                }
            }
            finally {
                if (!cancelled && showLoading)
                    setLoading(false);
            }
        }
        // Initial / triggered fetch shows the spinner; polling refreshes silently.
        fetchOnce(true);
        // Also re-pull the Kill List + Parking Lot whenever the parent bumps
        // refreshTick (e.g. after a block is created). Without this the
        // kill-list panel kept stale rows after a schedule — the row stayed
        // visible and the user could double-schedule it.
        if (killListLoaded)
            void loadKillList();
        if (parkingLotLoaded)
            void loadParkingLot();
        const interval = window.setInterval(() => {
            if (!document.hidden)
                fetchOnce(false);
        }, POLL_MS);
        function onFocus() {
            // Tab regained focus — pull fresh data immediately.
            fetchOnce(false);
        }
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onFocus);
        return () => {
            cancelled = true;
            window.clearInterval(interval);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onFocus);
        };
    }, [open, refreshTick, tick]);
    // Pull the caller's one-off tasks for the "One Off Tasks" section.
    // Separate from the main feed (one-off projects are deliberately kept
    // out of /me/schedulable-items). Refreshes on open + parent refreshTick.
    useEffect(() => {
        if (!open)
            return;
        let cancelled = false;
        (async () => {
            try {
                const feed = await api.get("/users/me/one-off-tasks");
                if (!cancelled)
                    setOneOffTasks(feed.tasks);
            }
            catch {
                /* non-critical — leave the section empty on failure */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, refreshTick, tick]);
    // The standalone Claim buttons were removed when "+ Kill" became the
    // single auto-claim entry point on Mgmt Assigned. We still need
    // claimProject below for the Parking Lot "Volunteer" button on
    // unassigned BU projects.
    async function claimProject(projectId) {
        // Project-level self-claim — caller becomes a Project Assignment, which
        // also removes the project from the Parking Lot's unassigned-BU bucket
        // on the next /parking-lot refresh.
        setClaiming(projectId);
        try {
            await api.post(`/projects/${projectId}/claim`);
            setTick((t) => t + 1);
            await loadParkingLot();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't claim project");
        }
        finally {
            setClaiming(null);
        }
    }
    async function claimTask(taskId) {
        // Task-level self-claim — same idea as claimProject but for the new
        // "orphan tasks inside partially-claimed projects" bucket. POST
        // /tasks/{id}/claim is idempotent (no-op if already an assignee).
        // After the call we refetch the Parking Lot so the row drops off.
        setClaiming(taskId);
        try {
            await api.post(`/tasks/${taskId}/claim`);
            setTick((t) => t + 1);
            await loadParkingLot();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't claim task");
        }
        finally {
            setClaiming(null);
        }
    }
    function manualRefresh() {
        setTick((t) => t + 1);
        // All three secondary widgets render at once now, so a refresh
        // re-pulls everything visible.
        void loadKillList();
        void loadParkingLot();
    }
    // Lazy load Kill List / Parking Lot when their tabs are opened. Each is
    // tracked with a `*Loaded` flag so we don't refetch every render.
    async function loadKillList() {
        try {
            // include_scheduled=true so rows that already have a calendar
            // block stay visible (greyed) instead of disappearing. Without
            // this, a successful reschedule into a conflicting slot would
            // hide the row entirely — the user reported this as "Calendar
            // Kill List Rescheduling vanished after I scheduled it on a
            // busy time slot." The kill-row renderer already styles
            // scheduled rows as greyed + non-clickable with an "Added to
            // schedule" pill, so adding them back is purely additive UX.
            const rows = await api.get("/kill-list?include_scheduled=true");
            setKillList(rows);
            setKillListLoaded(true);
        }
        catch {
            /* leave previous data on screen */
        }
    }
    async function loadParkingLot() {
        try {
            // GET /parking-lot returns a combined feed:
            //   { suggestions, unassigned_bu_projects, unassigned_bu_tasks }
            // unassigned_bu_tasks may be missing on a stale server build —
            // default to [] so older deploys keep working during a rollout.
            const feed = await api.get("/parking-lot");
            setUnassignedBuProjects(feed.unassigned_bu_projects);
            setUnassignedBuTasks(feed.unassigned_bu_tasks ?? []);
            setParkingLotLoaded(true);
        }
        catch {
            /* leave previous data on screen */
        }
    }
    useEffect(() => {
        if (!open)
            return;
        // Both feed widgets render simultaneously on open — load both up front
        // and let the polling effect above keep them fresh.
        if (!killListLoaded)
            loadKillList();
        if (!parkingLotLoaded)
            loadParkingLot();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // ── Kill List actions ────────────────────────────────────────────────────
    async function addToKillList(opts) {
        try {
            const body = {};
            if (opts.taskId)
                body.task_id = opts.taskId;
            if (opts.checklistItemId)
                body.checklist_item_id = opts.checklistItemId;
            if (opts.parkingLotId)
                body.parking_lot_id = opts.parkingLotId;
            await api.post("/kill-list", body);
            await loadKillList();
        }
        catch (e) {
            // 409 Conflict has two distinct shapes from the backend:
            //   - dedup ("already on your kill list this week")
            //   - dependency-gate ("waiting on an earlier step — finish X first")
            // Surface the server's actual detail message so a blocked-task
            // attempt reads as "complete this predecessor first" rather than
            // the misleading dedup line we used to hardcode.
            if (e instanceof ApiError) {
                notify.error(e.message || "Couldn't add to your kill list");
                return;
            }
            notify.error(e instanceof Error ? e.message : "Couldn't add to your kill list");
        }
    }
    async function removeFromKillList(id) {
        try {
            await api.del(`/kill-list/${id}`);
            await loadKillList();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't remove");
        }
    }
    // Open the reassignment modal — caller picks a teammate + optional note
    // and submits a handoff request on the kill row's underlying task.
    // Workers' handoffs need manager/admin approval (same flow as the
    // Complete? "Hand off to a coworker" path).
    function requestKillReassign(row) {
        if (!row.task_id && !row.checklist_item_id) {
            notify.error("Parking Lot suggestions can't be reassigned — only tasks.");
            return;
        }
        setReassignKillRow(row);
    }
    // Create a one-off task from the Kill List tab — spins up a one_off_task
    // project (via /projects/quick-track) and immediately adds the resulting
    // checklist item to the caller's Kill List so it's ready to schedule.
    async function addOneOffTaskToKillList(p) {
        try {
            const r = await api.post("/projects/quick-track", {
                title: p.title,
                activity_id: p.activityId,
            });
            // Drop it straight onto the user's Kill List so the new task is one
            // click away from being scheduled as a Deep Focus block.
            await api.post("/kill-list", { checklist_item_id: r.checklist_item_id });
            await loadKillList();
            setTick((t) => t + 1); // refresh scheduler too — new project exists
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't create one-off task");
        }
    }
    // ── Parking Lot actions ──────────────────────────────────────────────────
    async function submitParkingLot(p) {
        try {
            await api.post("/parking-lot", p);
            await loadParkingLot();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't submit");
        }
    }
    // Build the grouped bins once per data tick — the bin layout is pure and
    // doesn't depend on local state, so useMemo on the data ref is enough.
    const bins = useMemo(() => (data ? buildBins(data) : []), [data]);
    if (!open)
        return null;
    const recurringTasks = data?.recurring_event_tasks ?? [];
    const patterns = data?.recurring_patterns ?? [];
    // Empty = no projects in scope at all (and no recurring meetings / patterns).
    // Even projects with nothing for the caller to do still render as cards, so
    // we don't hide a BU just because every item is taken by other teammates.
    const binsHaveAnything = bins.some((b) => b.projects.length > 0);
    const isEmpty = !loading && !binsHaveAnything && recurringTasks.length === 0 && patterns.length === 0;
    return (_jsxs("aside", { className: "schedulable-panel", children: [_jsxs("header", { className: "schedulable-head", children: [_jsx("h3", { children: "Schedule" }), _jsxs("div", { className: "schedulable-head-actions", children: [_jsx("button", { className: "btn-link", onClick: manualRefresh, disabled: loading, title: lastFetchedAt
                                    ? `Last refreshed ${lastFetchedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
                                    : "Refresh", children: loading ? "Refreshing…" : "↻ Refresh" }), _jsx("button", { className: "btn-link", onClick: onClose, children: "Close" })] })] }), _jsx("div", { className: "schedulable-search", children: _jsx(CalendarSearch, { onScheduled: () => {
                        manualRefresh();
                        onScheduled?.();
                    } }) }), loading && _jsx("div", { className: "muted", children: "Loading\u2026" }), fetchError && (_jsxs("div", { className: "error", style: { marginBottom: "0.6rem" }, children: [fetchError, data && (_jsxs("span", { className: "muted small", children: [" ", "\u00B7 showing the last data we had"] }))] })), isEmpty && (_jsx("div", { className: "muted small", children: "No projects in your business unit yet. Once an admin adds a project to one of your units (or creates a recurring-event project), it'll show up here." })), _jsxs("div", { className: "schedulable-grid", children: [_jsxs("div", { className: `schedulable-widget ${expandedWidgets.has("mgmt") ? "expanded" : "collapsed"}`, children: [_jsxs("button", { type: "button", className: "schedulable-widget-head schedulable-widget-toggle", onClick: () => toggleWidget("mgmt"), "aria-expanded": expandedWidgets.has("mgmt"), children: [_jsxs("h4", { children: ["Mgmt Assigned", " ", (() => {
                                                // Same "what's on my plate" filter the expanded body
                                                // uses below — surface the count on the collapsed
                                                // header so users see at a glance whether anything
                                                // is waiting for them without opening the widget.
                                                // Mirrors the Kill List + Parking Lot header counts.
                                                const n = bins
                                                    .flatMap((b) => b.projects)
                                                    .filter((p) => p.assigned_items.length > 0 ||
                                                    p.assigned_tasks.length > 0).length;
                                                return n > 0 ? (_jsxs("span", { className: "muted", style: { fontWeight: 400 }, children: ["\u00B7 ", n] })) : null;
                                            })()] }), _jsx("span", { className: "widget-toggle-icon", "aria-hidden": "true", children: expandedWidgets.has("mgmt") ? "−" : "+" })] }), expandedWidgets.has("mgmt") &&
                                (() => {
                                    // Show projects where I personally have something to do:
                                    // an assigned task, checklist item, OR project-level
                                    // membership even if nobody has assigned me to a task
                                    // yet. The empty case (project assignment but no
                                    // tasks) renders a placeholder pointing the user to
                                    // Projects to set things up — without this surfacing,
                                    // a freshly-staffed project disappears between the
                                    // Parking Lot and Mgmt Assigned with nothing in front
                                    // of the assignee to act on.
                                    const myName = currentUser?.name ?? null;
                                    const iAmOnProjectTeam = (p) => {
                                        if (!myName)
                                            return false;
                                        return (p.assignee_names ?? []).includes(myName);
                                    };
                                    const allProjects = bins
                                        .flatMap((b) => b.projects)
                                        .filter((p) => p.assigned_items.length > 0 ||
                                        p.assigned_tasks.length > 0 ||
                                        iAmOnProjectTeam(p));
                                    const unified = [
                                        ...allProjects.map((p) => ({
                                            kind: "project",
                                            due_date: p.due_date,
                                            // Priority is now nullable (N/A). Treat null as
                                            // POSITIVE_INFINITY so it sorts last in priority
                                            // ties — matches the "N/A goes to the bottom" spec.
                                            priority: p.priority,
                                            title: p.name,
                                            project: p,
                                        })),
                                        ...oneOffTasks
                                            .filter((o) => o.assigned_to_me && !o.is_completed)
                                            .map((o) => ({
                                            kind: "oneoff",
                                            due_date: o.due_date ?? null,
                                            // One-off tasks don't have an explicit priority on
                                            // the table — they sort as N/A (last in priority
                                            // ties at the same due date).
                                            priority: null,
                                            title: o.title,
                                            task: o,
                                        })),
                                    ];
                                    unified.sort((a, b) => {
                                        const aDue = a.due_date
                                            ? new Date(a.due_date).getTime()
                                            : Number.POSITIVE_INFINITY;
                                        const bDue = b.due_date
                                            ? new Date(b.due_date).getTime()
                                            : Number.POSITIVE_INFINITY;
                                        if (aDue !== bDue)
                                            return aDue - bDue;
                                        const aPr = a.priority == null ? Number.POSITIVE_INFINITY : a.priority;
                                        const bPr = b.priority == null ? Number.POSITIVE_INFINITY : b.priority;
                                        if (aPr !== bPr)
                                            return aPr - bPr;
                                        return a.title.localeCompare(b.title);
                                    });
                                    if (unified.length === 0) {
                                        return (_jsx("div", { className: "muted small", children: "Nothing on your plate right now. Check the Parking Lot for unassigned BU projects you can volunteer for." }));
                                    }
                                    const killedTaskIds = new Set(killList.map((k) => k.task_id).filter(Boolean));
                                    const killedItemIds = new Set(killList.map((k) => k.checklist_item_id).filter(Boolean));
                                    return (_jsxs(_Fragment, { children: [_jsxs("p", { className: "muted small", style: { marginTop: 0 }, children: [unified.length, " on your plate \u2014 sorted by due date, then priority."] }), unified.map((u) => {
                                                if (u.kind === "project") {
                                                    return (_jsx(ProjectCard, { project: u.project, sort: sort, onScheduleItem: onScheduleAsBlock, onScheduleTask: onScheduleTaskAsBlock, onScheduleProject: onScheduleProjectAsBlock, onAddToKillList: (opts) => addToKillList(opts), onNudge: (blockers) => setNudgeBlockers(blockers), killedTaskIds: killedTaskIds, killedItemIds: killedItemIds }, `p:${u.project.id}`));
                                                }
                                                // One-off task row — interleaved with projects so
                                                // due-date sort is honored across both kinds. Kept
                                                // intentionally lean: title, due, on-calendar/on-
                                                // kill-list status, + Kill button.
                                                const o = u.task;
                                                const scheduled = !!o.scheduled_at;
                                                const killed = killedTaskIds.has(o.task_id);
                                                return (_jsxs("div", { className: `schedulable-item schedulable-oneoff-inline ${scheduled || killed
                                                        ? "schedulable-item-scheduled"
                                                        : ""}`, children: [_jsxs("div", { className: "item-title", children: [_jsx("span", { className: "pill", style: { marginRight: 6 }, children: "\u26A1 One-off" }), o.title] }), _jsxs("div", { className: "item-meta", children: [o.due_date && (_jsxs("span", { className: `pill due-pill ${isOverdue(o.due_date) ? "overdue" : ""}`, title: `Due ${o.due_date}`, children: ["Due ", fmtDueDate(o.due_date)] })), o.category_label && (_jsx("span", { className: "pill", children: o.category_label }))] }), _jsx("div", { className: "item-actions", children: killed ? (_jsx("span", { className: "pill ok", title: "Already on your Kill List this week", children: "\u2713 On Kill List" })) : scheduled ? (_jsx("span", { className: "pill ok", title: "Already on your calendar", children: "\uD83D\uDCC5 On your calendar" })) : (_jsx("button", { type: "button", className: "btn-primary claim-btn", onClick: (e) => {
                                                                    e.stopPropagation();
                                                                    void addToKillList({ taskId: o.task_id });
                                                                }, title: "Add to this week's Kill List, then schedule it", children: "+ Kill" })) })] }, `o:${o.task_id}`));
                                            })] }));
                                })(), false && expandedWidgets.has("mgmt") &&
                                (() => {
                                    const mine = oneOffTasks.filter((o) => o.assigned_to_me && !o.is_completed);
                                    if (mine.length === 0)
                                        return null;
                                    const ooKilledTaskIds = new Set(killList.map((k) => k.task_id).filter(Boolean));
                                    // Sort by due date ascending (soonest first) — Joe's spec:
                                    // "filtered by due date in the same area instead of being
                                    // separated by categories." Unscheduled rows still come
                                    // before scheduled ones so the action-needed work floats up;
                                    // within each group, due date is the primary key. Missing
                                    // due_date is treated as the far future so dated items
                                    // always lead.
                                    mine.sort((a, b) => {
                                        const as = a.scheduled_at ? 1 : 0;
                                        const bs = b.scheduled_at ? 1 : 0;
                                        if (as !== bs)
                                            return as - bs; // unscheduled first
                                        const ad = a.due_date || "9999-12-31";
                                        const bd = b.due_date || "9999-12-31";
                                        if (ad !== bd)
                                            return ad.localeCompare(bd);
                                        return a.title.localeCompare(b.title);
                                    });
                                    return (_jsxs("div", { className: "schedulable-oneoff-section", children: [_jsxs("div", { className: "schedulable-oneoff-head", children: ["\u26A1 One Off Tasks", _jsx("span", { className: "muted small", children: ` · ${mine.length}` })] }), _jsx("ul", { className: "schedulable-list", children: mine.map((o) => {
                                                    const scheduled = !!o.scheduled_at;
                                                    const killed = ooKilledTaskIds.has(o.task_id);
                                                    return (_jsxs("li", { className: `schedulable-item ${scheduled || killed
                                                            ? "schedulable-item-scheduled"
                                                            : ""}`, children: [_jsx("div", { className: "item-title", children: o.title }), _jsxs("div", { className: "item-meta", children: [o.due_date && (_jsxs("span", { className: `pill due-pill ${isOverdue(o.due_date) ? "overdue" : ""}`, title: `Due ${o.due_date}`, children: ["Due ", fmtDueDate(o.due_date)] })), o.category_label && (_jsx("span", { className: "pill", title: "Category tag \u2014 not used for grouping anymore; the list is sorted by due date", children: o.category_label }))] }), _jsx("div", { className: "item-actions", children: killed ? (_jsx("span", { className: "pill ok", title: "Already on your Kill List this week", children: "\u2713 On Kill List" })) : scheduled ? (_jsx("span", { className: "pill ok", title: "Already on your calendar", children: "\uD83D\uDCC5 On your calendar" })) : (_jsx("button", { type: "button", className: "btn-primary claim-btn", onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        void addToKillList({ taskId: o.task_id });
                                                                    }, title: "Add to this week's Kill List, then schedule it", children: "+ Kill" })) })] }, o.task_id));
                                                }) })] }));
                                })()] }), _jsxs("div", { className: `schedulable-widget ${expandedWidgets.has("kill") ? "expanded" : "collapsed"}`, children: [_jsxs("button", { type: "button", className: "schedulable-widget-head schedulable-widget-toggle", onClick: () => toggleWidget("kill"), "aria-expanded": expandedWidgets.has("kill"), children: [_jsxs("h4", { children: ["Kill List", " ", killList.length > 0 && (_jsxs("span", { className: "muted", style: { fontWeight: 400 }, children: ["\u00B7 ", killList.length] }))] }), _jsx("span", { className: "widget-toggle-icon", "aria-hidden": "true", children: expandedWidgets.has("kill") ? "−" : "+" })] }), expandedWidgets.has("kill") && (_jsx(KillListTabView, { rows: killList, loaded: killListLoaded, onRemove: removeFromKillList, onAddOneOff: addOneOffTaskToKillList, onSchedule: onScheduleKillItem, onRequestReassign: requestKillReassign }))] }), _jsxs("div", { className: `schedulable-widget ${expandedWidgets.has("parking") ? "expanded" : "collapsed"}`, children: [_jsxs("button", { type: "button", className: "schedulable-widget-head schedulable-widget-toggle", onClick: () => toggleWidget("parking"), "aria-expanded": expandedWidgets.has("parking"), children: [_jsxs("h4", { children: ["Parking Lot", " ", unassignedBuProjects.length + unassignedBuTasks.length > 0 && (_jsxs("span", { className: "muted", style: { fontWeight: 400 }, children: ["\u00B7 ", unassignedBuProjects.length + unassignedBuTasks.length] }))] }), _jsx("span", { className: "widget-toggle-icon", "aria-hidden": "true", children: expandedWidgets.has("parking") ? "−" : "+" })] }), expandedWidgets.has("parking") && (_jsx(ParkingLotTabView, { unassignedBuProjects: unassignedBuProjects, unassignedBuTasks: unassignedBuTasks, claimingId: claiming, onSubmit: submitParkingLot, onClaimProject: claimProject, onClaimTask: claimTask }))] }), _jsxs("div", { className: `schedulable-widget ${expandedWidgets.has("recurring") ? "expanded" : "collapsed"}`, children: [_jsxs("button", { type: "button", className: "schedulable-widget-head schedulable-widget-toggle", onClick: () => toggleWidget("recurring"), "aria-expanded": expandedWidgets.has("recurring"), children: [_jsx("h4", { children: "Recurring events" }), _jsx("span", { className: "widget-toggle-icon", "aria-hidden": "true", children: expandedWidgets.has("recurring") ? "−" : "+" })] }), expandedWidgets.has("recurring") && (_jsxs(_Fragment, { children: [recurringTasks.length === 0 && patterns.length === 0 && (_jsx("div", { className: "muted small", children: "No recurring events to set up \u2014 admins add these as recurring project templates." })), recurringTasks.length > 0 && (_jsxs(_Fragment, { children: [_jsx("p", { className: "muted small scheduler-claim-hint", children: "Click a meeting to put it on your calendar. The admin's schedule is a default \u2014 you can adjust before saving." }), _jsx("ul", { className: "schedulable-list", children: recurringTasks.map((t) => {
                                                    const preview = fmtTaskRecurrence(t);
                                                    return (_jsxs("li", { className: "schedulable-item recurring", onClick: () => onScheduleAsRecurring(t), title: "Click to choose your recurrence and add it to your calendar", children: [_jsx("div", { className: "item-title", children: t.title }), _jsxs("div", { className: "item-meta", children: [_jsx("span", { children: t.project_name }), _jsx("span", { className: "pill recurring-pill", children: "\u21BB Recurring" })] }), preview ? (_jsx("div", { className: "item-when", children: preview })) : (_jsx("div", { className: "item-when muted", children: "No default schedule yet \u2014 click to set yours." })), _jsx(DueDates, { taskDue: t.task_due_date, projectDue: t.project_due_date }), _jsxs("div", { className: "item-actions", children: [_jsx("button", { type: "button", className: "btn-primary claim-btn", onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            onScheduleAsRecurring(t);
                                                                        }, children: "Schedule on my calendar" }), _jsx(OpenProjectLink, { projectId: t.project_id, label: "Open meeting \u2197" })] })] }, t.task_id));
                                                }) })] })), patterns.length > 0 && (_jsxs(_Fragment, { children: [_jsx("h5", { className: "schedulable-subhead", children: "Your existing patterns" }), _jsx("ul", { className: "schedulable-list", children: patterns.map((p) => (_jsx(RecurringPatternRow, { pattern: p }, p.id))) })] }))] }))] }), _jsx(PersonalTodoCell, { expanded: expandedWidgets.has("personal"), onToggle: () => toggleWidget("personal"), onSchedule: onSchedulePersonalTodo, refreshTick: refreshTick }), _jsx(SchedulerGoalsCell, { expanded: expandedWidgets.has("goals"), onToggle: () => toggleWidget("goals"), onSchedule: onScheduleGoal, onScheduleDays: onScheduleGoalDays })] }), reassignKillRow && (_jsx(ReassignHandoffModal, { kill: reassignKillRow, onClose: () => setReassignKillRow(null), onSubmitted: () => {
                    setReassignKillRow(null);
                    // Refresh kill list — the row is still on it until admin
                    // approves the handoff, but a future enhancement could
                    // surface "Reassign pending" inline.
                    void loadKillList();
                } })), _jsx(NudgeComposer, { open: nudgeBlockers !== null, blockers: nudgeBlockers ?? [], onClose: () => setNudgeBlockers(null) })] }));
}
